import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

/* ================================================================
   DatabaseTool — Web-based MySQL client for Superadmin
   ================================================================ */

// ─── Toast ──────────────────────────────────────────────────────
function Toast({ msg, type, onClose }) {
  if (!msg) return null;
  const bg = type === 'error' ? 'bg-red-600' : type === 'success' ? 'bg-green-600' : 'bg-blue-600';
  return (
    <div className={`fixed top-4 right-4 z-[9999] ${bg} text-white px-5 py-3 rounded-lg shadow-lg flex items-center gap-3 max-w-lg`}>
      <span className="flex-1 text-sm">{msg}</span>
      <button onClick={onClose} className="text-white/80 hover:text-white font-bold text-lg leading-none">&times;</button>
    </div>
  );
}

// ─── Confirm Modal ──────────────────────────────────────────────
function ConfirmModal({ open, title, body, onConfirm, onCancel, danger }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-lg font-bold mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-5 whitespace-pre-wrap">{body}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 text-sm text-white rounded-lg ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DatabaseTool() {
  /* ─── State ──────────────────────────────────────────────────── */
  const [databases, setDatabases] = useState([]);
  const [selectedDb, setSelectedDb] = useState(null);
  const [dbObjects, setDbObjects] = useState(null);
  const [activeTab, setActiveTab] = useState('tables');     // tables | views | procedures | functions | query
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({ tables: true, views: false, procedures: false, functions: false });

  // Table browser
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableView, setTableView] = useState('data');        // data | structure
  const [tableData, setTableData] = useState(null);
  const [tableStructure, setTableStructure] = useState(null);
  const [dataPage, setDataPage] = useState(0);
  const [dataLimit] = useState(50);
  const [dataSearch, setDataSearch] = useState('');
  const [dataSort, setDataSort] = useState({ col: null, dir: 'ASC' });
  const [tableDataLoading, setTableDataLoading] = useState(false);

  // Source viewer (SP / View / Function)
  const [sourceCode, setSourceCode] = useState(null);
  const [sourceObject, setSourceObject] = useState(null);

  // Query window
  const [querySql, setQuerySql] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryHistory, setQueryHistory] = useState([]);

  // Toast / confirm
  const [toast, setToast] = useState({ msg: '', type: '' });
  const [confirm, setConfirm] = useState({ open: false });

  const textareaRef = useRef(null);

  const showToast = (msg, type = 'info') => { setToast({ msg, type }); setTimeout(() => setToast({ msg: '', type: '' }), 4000); };

  /* ─── Load databases on mount ────────────────────────────────── */
  useEffect(() => { loadDatabases(); }, []);

  const loadDatabases = async () => {
    try {
      const { data } = await api.get('/db-tool/databases');
      if (data.success) setDatabases(data.data);
    } catch (e) { showToast('Failed to load databases', 'error'); }
  };

  /* ─── Select database → load objects ─────────────────────────── */
  const selectDatabase = useCallback(async (db) => {
    setSelectedDb(db);
    setSelectedTable(null);
    setTableData(null);
    setTableStructure(null);
    setSourceCode(null);
    setSourceObject(null);
    setQueryResult(null);
    setActiveTab('tables');
    setLoading(true);
    try {
      const { data } = await api.get(`/db-tool/${db.name}/objects`);
      if (data.success) setDbObjects(data.data);
    } catch (e) { showToast('Failed to load database objects', 'error'); }
    setLoading(false);
  }, []);

  /* ─── Select table → load data ───────────────────────────────── */
  const selectTable = useCallback(async (tableName) => {
    setSelectedTable(tableName);
    setTableView('data');
    setDataPage(0);
    setDataSearch('');
    setDataSort({ col: null, dir: 'ASC' });
    await loadTableData(tableName, 0, '', null, 'ASC');
    await loadTableStructure(tableName);
  }, [selectedDb]);

  const loadTableData = async (table, offset = 0, search = '', orderBy = null, orderDir = 'ASC') => {
    if (!selectedDb) return;
    setTableDataLoading(true);
    try {
      const params = new URLSearchParams({ limit: dataLimit, offset, ...(search && { search }), ...(orderBy && { orderBy, orderDir }) });
      const { data } = await api.get(`/db-tool/${selectedDb.name}/tables/${table}/data?${params}`);
      if (data.success) setTableData(data.data);
    } catch (e) { showToast('Failed to load table data', 'error'); }
    setTableDataLoading(false);
  };

  const loadTableStructure = async (table) => {
    if (!selectedDb) return;
    try {
      const { data } = await api.get(`/db-tool/${selectedDb.name}/tables/${table}/structure`);
      if (data.success) setTableStructure(data.data);
    } catch (e) { showToast('Failed to load table structure', 'error'); }
  };

  /* ─── View source (procedure / view / function) ─────────────── */
  const viewSource = async (type, name) => {
    if (!selectedDb) return;
    try {
      const { data } = await api.get(`/db-tool/${selectedDb.name}/source/${type}/${name}`);
      if (data.success) { setSourceCode(data.data.source); setSourceObject({ type, name }); }
    } catch (e) { showToast('Failed to load source', 'error'); }
  };

  /* ─── Execute query ─────────────────────────────────────────── */
  const executeQuery = async () => {
    if (!selectedDb || !querySql.trim()) return;
    setQueryLoading(true);
    setQueryResult(null);
    try {
      const { data } = await api.post(`/db-tool/${selectedDb.name}/query`, { sql: querySql.trim() });
      if (data.success) {
        setQueryResult(data.data);
        setQueryHistory(h => [{ sql: querySql.trim(), time: new Date(), result: data.data }, ...h].slice(0, 50));
        if (data.data.type === 'execution') showToast(data.data.message, 'success');
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      setQueryResult({ type: 'error', message: msg, errno: e.response?.data?.errno, sqlState: e.response?.data?.sqlState });
      showToast(msg, 'error');
    }
    setQueryLoading(false);
  };

  /* ─── Pagination handlers ───────────────────────────────────── */
  const handlePageChange = (newOffset) => {
    setDataPage(newOffset);
    loadTableData(selectedTable, newOffset, dataSearch, dataSort.col, dataSort.dir);
  };

  const handleSearch = (val) => {
    setDataSearch(val);
    setDataPage(0);
    loadTableData(selectedTable, 0, val, dataSort.col, dataSort.dir);
  };

  const handleSort = (col) => {
    const newDir = dataSort.col === col && dataSort.dir === 'ASC' ? 'DESC' : 'ASC';
    setDataSort({ col, dir: newDir });
    setDataPage(0);
    loadTableData(selectedTable, 0, dataSearch, col, newDir);
  };

  /* ─── Keyboard shortcut for query ──────────────────────────── */
  const handleQueryKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); executeQuery(); }
  };

  /* ================================================================
     RENDER
     ================================================================ */
  return (
    <div className="flex flex-col h-[calc(100vh-64px)] overflow-hidden">
      <Toast msg={toast.msg} type={toast.type} onClose={() => setToast({ msg: '', type: '' })} />
      <ConfirmModal {...confirm} />

      {/* ─── Top Bar ─────────────────────────────────────────────── */}
      <div className="px-6 py-3 border-b bg-white flex items-center gap-4 flex-shrink-0">
        <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" /></svg>
          Database Tool
        </h1>
        {/* Database selector */}
        <select
          className="border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-w-[280px]"
          value={selectedDb?.name || ''}
          onChange={e => {
            const db = databases.find(d => d.name === e.target.value);
            if (db) selectDatabase(db);
          }}
        >
          <option value="">Select a database...</option>
          {databases.map(db => (
            <option key={db.name} value={db.name} disabled={!db.exists}>
              {db.label} {!db.exists ? '(not found)' : ''}
            </option>
          ))}
        </select>
        {selectedDb && dbObjects && (
          <span className="text-xs text-gray-500">
            {dbObjects.summary.tables}T &middot; {dbObjects.summary.views}V &middot; {dbObjects.summary.procedures}SP &middot; {dbObjects.summary.functions}FN
          </span>
        )}
      </div>

      {/* ─── Main Body — sidebar + content ──────────────────────── */}
      {!selectedDb ? (
        <EmptyState />
      ) : loading ? (
        <div className="flex-1 flex items-center justify-center"><Spinner /> Loading...</div>
      ) : (
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar — object browser (accordion) */}
          <div className="w-64 border-r bg-gray-50 flex flex-col overflow-hidden flex-shrink-0">
            <div className="flex-1 overflow-y-auto">
              {/* ── Tables accordion ─────────────────────────── */}
              <AccordionSection
                title="Tables" count={dbObjects?.tables?.length || 0}
                icon="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                open={expanded.tables}
                onToggle={() => setExpanded(e => ({ ...e, tables: !e.tables }))}
              >
                {dbObjects?.tables?.map(t => (
                  <button key={t.name} onClick={() => { setActiveTab('tables'); selectTable(t.name); }}
                    className={`w-full text-left px-3 py-1.5 text-xs rounded-md flex justify-between items-center ${selectedTable === t.name && activeTab === 'tables' ? 'bg-indigo-100 text-indigo-700 font-medium' : 'hover:bg-gray-100 text-gray-700'}`}>
                    <span className="truncate">{t.name}</span>
                    <span className="text-[10px] text-gray-400 ml-1 flex-shrink-0">{t.row_count ?? ''}</span>
                  </button>
                ))}
              </AccordionSection>

              {/* ── Views accordion ──────────────────────────── */}
              <AccordionSection
                title="Views" count={dbObjects?.views?.length || 0}
                icon="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                open={expanded.views}
                onToggle={() => setExpanded(e => ({ ...e, views: !e.views }))}
              >
                {dbObjects?.views?.map(v => (
                  <button key={v.name} onClick={() => { setActiveTab('views'); viewSource('view', v.name); }}
                    className={`w-full text-left px-3 py-1.5 text-xs rounded-md ${sourceObject?.name === v.name && sourceObject?.type === 'view' ? 'bg-indigo-100 text-indigo-700 font-medium' : 'hover:bg-gray-100 text-gray-700'}`}>
                    <span className="truncate">{v.name}</span>
                  </button>
                ))}
                {(dbObjects?.views?.length || 0) === 0 && <p className="text-[11px] text-gray-400 px-3 py-1 italic">No views</p>}
              </AccordionSection>

              {/* ── Stored Procedures accordion ──────────────── */}
              <AccordionSection
                title="Procedures" count={dbObjects?.procedures?.length || 0}
                icon="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                open={expanded.procedures}
                onToggle={() => setExpanded(e => ({ ...e, procedures: !e.procedures }))}
              >
                {dbObjects?.procedures?.map(p => (
                  <button key={p.name} onClick={() => { setActiveTab('procedures'); viewSource('procedure', p.name); }}
                    className={`w-full text-left px-3 py-1.5 text-xs rounded-md ${sourceObject?.name === p.name && sourceObject?.type === 'procedure' ? 'bg-indigo-100 text-indigo-700 font-medium' : 'hover:bg-gray-100 text-gray-700'}`}>
                    <span className="truncate">{p.name}</span>
                  </button>
                ))}
                {(dbObjects?.procedures?.length || 0) === 0 && <p className="text-[11px] text-gray-400 px-3 py-1 italic">No procedures</p>}
              </AccordionSection>

              {/* ── Functions accordion ──────────────────────── */}
              <AccordionSection
                title="Functions" count={dbObjects?.functions?.length || 0}
                icon="M4 6h16M4 10h16M4 14h16M4 18h16"
                open={expanded.functions}
                onToggle={() => setExpanded(e => ({ ...e, functions: !e.functions }))}
              >
                {dbObjects?.functions?.map(f => (
                  <button key={f.name} onClick={() => { setActiveTab('functions'); viewSource('function', f.name); }}
                    className={`w-full text-left px-3 py-1.5 text-xs rounded-md ${sourceObject?.name === f.name && sourceObject?.type === 'function' ? 'bg-indigo-100 text-indigo-700 font-medium' : 'hover:bg-gray-100 text-gray-700'}`}>
                    <span className="truncate">{f.name}</span>
                  </button>
                ))}
                {(dbObjects?.functions?.length || 0) === 0 && <p className="text-[11px] text-gray-400 px-3 py-1 italic">No functions</p>}
              </AccordionSection>

              {/* ── Query Window shortcut ────────────────────── */}
              <div className="border-t px-2 py-2">
                <button onClick={() => setActiveTab('query')}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md flex items-center gap-2 ${activeTab === 'query' ? 'bg-indigo-100 text-indigo-700 font-medium' : 'hover:bg-gray-100 text-gray-600'}`}>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>
                  Query Window
                </button>
              </div>
            </div>
          </div>

          {/* ─── Content pane ────────────────────────────────────── */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Show Query Window */}
            {activeTab === 'query' && (
              <QueryWindow
                sql={querySql} setSql={setQuerySql}
                result={queryResult} loading={queryLoading}
                onExecute={executeQuery} onKeyDown={handleQueryKeyDown}
                history={queryHistory} textareaRef={textareaRef}
                dbName={selectedDb.name}
              />
            )}

            {/* Show Source Code for views/procedures/functions */}
            {activeTab !== 'query' && sourceCode !== null && (
              <SourceViewer source={sourceCode} object={sourceObject} onClose={() => { setSourceCode(null); setSourceObject(null); }} />
            )}

            {/* Show Table Data / Structure */}
            {activeTab === 'tables' && selectedTable && sourceCode === null && (
              <TableViewer
                table={selectedTable} view={tableView} setView={setTableView}
                data={tableData} structure={tableStructure}
                loading={tableDataLoading}
                page={dataPage} limit={dataLimit} search={dataSearch}
                sort={dataSort}
                onPageChange={handlePageChange}
                onSearch={handleSearch}
                onSort={handleSort}
              />
            )}

            {/* Default — no selection */}
            {activeTab !== 'query' && !selectedTable && sourceCode === null && (
              <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                Select an object from the sidebar to browse
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ================================================================
   Sub-Components
   ================================================================ */

function EmptyState() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
      <svg className="w-16 h-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" /></svg>
      <p className="text-lg font-medium mb-1">Database Management Tool</p>
      <p className="text-sm">Select a database from the dropdown above to get started</p>
    </div>
  );
}

function Spinner() {
  return <svg className="animate-spin h-5 w-5 text-indigo-600 mr-2" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>;
}

/* ─── Accordion Section for sidebar ───────────────────────────── */
function AccordionSection({ title, count, icon, open, onToggle, children }) {
  return (
    <div className="border-b border-gray-200">
      <button onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-100 transition-colors">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={icon} />
          </svg>
          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{title}</span>
          <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full font-medium">{count}</span>
        </div>
        <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-0.5 max-h-[40vh] overflow-y-auto">
          {children}
        </div>
      )}
    </div>
  );
}

/* ─── Table Viewer (Data + Structure) ─────────────────────────── */
function TableViewer({ table, view, setView, data, structure, loading, page, limit, search, sort, onPageChange, onSearch, onSort }) {
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(page / limit) + 1;
  const searchTimer = useRef(null);

  const handleSearchInput = (val) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => onSearch(val), 400);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-2.5 border-b bg-white flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <h2 className="font-semibold text-gray-800">{table}</h2>
          <span className="text-xs text-gray-400">{total} rows</span>
          <div className="flex bg-gray-100 rounded-lg overflow-hidden text-xs">
            <button onClick={() => setView('data')} className={`px-3 py-1 ${view === 'data' ? 'bg-indigo-600 text-white' : 'text-gray-600'}`}>Data</button>
            <button onClick={() => setView('structure')} className={`px-3 py-1 ${view === 'structure' ? 'bg-indigo-600 text-white' : 'text-gray-600'}`}>Structure</button>
          </div>
        </div>
        {view === 'data' && (
          <input
            type="text" placeholder="Search..." defaultValue={search}
            onChange={e => handleSearchInput(e.target.value)}
            className="border rounded-lg px-3 py-1 text-sm w-56 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        )}
      </div>

      {/* Data View */}
      {view === 'data' && (
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32"><Spinner /> Loading data...</div>
          ) : !data || data.rows.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">No data found</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 border-b">#</th>
                  {data.columns.map(col => (
                    <th key={col} onClick={() => onSort(col)}
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500 border-b cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap">
                      {col}
                      {sort.col === col && <span className="ml-1">{sort.dir === 'ASC' ? '▲' : '▼'}</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.rows.map((row, i) => (
                  <tr key={i} className="hover:bg-indigo-50/40">
                    <td className="px-3 py-1.5 text-xs text-gray-400 border-r">{page + i + 1}</td>
                    {data.columns.map(col => (
                      <td key={col} className="px-3 py-1.5 text-gray-700 max-w-[300px] truncate whitespace-nowrap" title={String(row[col] ?? '')}>
                        {row[col] === null ? <span className="text-gray-300 italic">NULL</span> : String(row[col])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Structure View */}
      {view === 'structure' && structure && (
        <div className="flex-1 overflow-auto p-4 space-y-6">
          {/* Columns */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Columns ({structure.columns.length})</h3>
            <table className="min-w-full text-sm border rounded-lg overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  {['#', 'Name', 'Type', 'Nullable', 'Key', 'Default', 'Extra'].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {structure.columns.map((c, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-1.5 text-gray-400 text-xs">{c.position}</td>
                    <td className="px-3 py-1.5 font-medium text-gray-800">{c.name}</td>
                    <td className="px-3 py-1.5 text-indigo-600 font-mono text-xs">{c.type}</td>
                    <td className="px-3 py-1.5">{c.nullable === 'YES' ? <span className="text-yellow-600">YES</span> : <span className="text-gray-400">NO</span>}</td>
                    <td className="px-3 py-1.5">
                      {c.key_type === 'PRI' && <span className="bg-yellow-100 text-yellow-800 text-xs px-1.5 py-0.5 rounded font-medium">PK</span>}
                      {c.key_type === 'UNI' && <span className="bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5 rounded font-medium">UNI</span>}
                      {c.key_type === 'MUL' && <span className="bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded font-medium">IDX</span>}
                    </td>
                    <td className="px-3 py-1.5 text-xs font-mono text-gray-500">{c.default_value ?? <span className="text-gray-300">NULL</span>}</td>
                    <td className="px-3 py-1.5 text-xs text-gray-500">{c.extra}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Foreign Keys */}
          {structure.foreignKeys?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Foreign Keys ({structure.foreignKeys.length})</h3>
              <table className="min-w-full text-sm border rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    {['Constraint', 'Column', 'References Table', 'References Column'].map(h => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {structure.foreignKeys.map((fk, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-1.5 text-gray-600">{fk.name}</td>
                      <td className="px-3 py-1.5 font-medium">{fk.column_name}</td>
                      <td className="px-3 py-1.5 text-indigo-600">{fk.ref_table}</td>
                      <td className="px-3 py-1.5">{fk.ref_column}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* CREATE TABLE */}
          {structure.createSql && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">CREATE Statement</h3>
              <pre className="bg-gray-900 text-green-400 text-xs p-4 rounded-lg overflow-auto max-h-64 whitespace-pre-wrap">{structure.createSql}</pre>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {view === 'data' && data && total > limit && (
        <div className="px-4 py-2 border-t bg-white flex items-center justify-between text-sm flex-shrink-0">
          <span className="text-gray-500">
            Showing {page + 1}-{Math.min(page + limit, total)} of {total}
          </span>
          <div className="flex gap-1">
            <button onClick={() => onPageChange(0)} disabled={page === 0}
              className="px-2 py-1 border rounded text-xs disabled:opacity-40 hover:bg-gray-50">&laquo;</button>
            <button onClick={() => onPageChange(Math.max(0, page - limit))} disabled={page === 0}
              className="px-2 py-1 border rounded text-xs disabled:opacity-40 hover:bg-gray-50">&lsaquo;</button>
            <span className="px-3 py-1 text-xs text-gray-600">Page {currentPage} of {totalPages}</span>
            <button onClick={() => onPageChange(page + limit)} disabled={page + limit >= total}
              className="px-2 py-1 border rounded text-xs disabled:opacity-40 hover:bg-gray-50">&rsaquo;</button>
            <button onClick={() => onPageChange((totalPages - 1) * limit)} disabled={page + limit >= total}
              className="px-2 py-1 border rounded text-xs disabled:opacity-40 hover:bg-gray-50">&raquo;</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Source Code Viewer ──────────────────────────────────────── */
function SourceViewer({ source, object, onClose }) {
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-4 py-2.5 border-b bg-white flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="bg-indigo-100 text-indigo-700 text-xs font-medium px-2 py-0.5 rounded uppercase">{object?.type}</span>
          <h2 className="font-semibold text-gray-800">{object?.name}</h2>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">&times;</button>
      </div>
      <div className="flex-1 overflow-auto">
        <pre className="bg-gray-900 text-green-400 text-xs p-4 min-h-full whitespace-pre-wrap font-mono">{source || '(No source available)'}</pre>
      </div>
    </div>
  );
}

/* ─── Query Window ─────────────────────────────────────────────── */
function QueryWindow({ sql, setSql, result, loading, onExecute, onKeyDown, history, textareaRef, dbName }) {
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Editor */}
      <div className="flex flex-col flex-shrink-0 border-b" style={{ height: '220px' }}>
        <div className="px-4 py-2 bg-gray-50 border-b flex items-center justify-between">
          <span className="text-xs font-medium text-gray-600">SQL Query — {dbName}</span>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-400">Ctrl+Enter to execute</span>
            <button onClick={onExecute} disabled={loading || !sql.trim()}
              className="bg-indigo-600 text-white px-4 py-1.5 text-xs rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5">
              {loading ? <Spinner /> : <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
              Execute
            </button>
          </div>
        </div>
        <textarea
          ref={textareaRef}
          value={sql} onChange={e => setSql(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Enter SQL query here... (SELECT, INSERT, UPDATE, DELETE, CREATE, ALTER, DROP)"
          className="flex-1 p-3 font-mono text-sm resize-none focus:outline-none bg-gray-900 text-green-400 placeholder-gray-600"
          spellCheck={false}
        />
      </div>

      {/* Results */}
      <div className="flex-1 overflow-auto">
        {loading && <div className="flex items-center justify-center h-32"><Spinner /> Executing query...</div>}

        {!loading && result?.type === 'error' && (
          <div className="p-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium text-sm">Error {result.errno ? `(${result.errno})` : ''}</p>
              <p className="text-red-600 text-sm mt-1 font-mono">{result.message}</p>
              {result.sqlState && <p className="text-red-400 text-xs mt-1">SQL State: {result.sqlState}</p>}
            </div>
          </div>
        )}

        {!loading && result?.type === 'execution' && (
          <div className="p-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800 font-medium text-sm">Query executed successfully</p>
              <div className="flex gap-6 mt-2 text-xs text-green-700">
                <span>Affected rows: {result.affectedRows}</span>
                {result.insertId > 0 && <span>Insert ID: {result.insertId}</span>}
                {result.changedRows > 0 && <span>Changed: {result.changedRows}</span>}
                <span>Duration: {result.duration}ms</span>
              </div>
            </div>
          </div>
        )}

        {!loading && result?.type === 'resultset' && (
          <div>
            <div className="px-4 py-2 bg-gray-50 border-b text-xs text-gray-600 flex items-center gap-4 flex-shrink-0">
              <span>{result.rowCount} row(s) returned</span>
              {result.truncated && <span className="text-orange-600">(Showing first 1000 rows)</span>}
              <span>{result.duration}ms</span>
            </div>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 border-b">#</th>
                  {result.columns.map(col => (
                    <th key={col} className="px-3 py-2 text-left text-xs font-medium text-gray-500 border-b whitespace-nowrap">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {result.rows.map((row, i) => (
                  <tr key={i} className="hover:bg-indigo-50/40">
                    <td className="px-3 py-1.5 text-xs text-gray-400 border-r">{i + 1}</td>
                    {result.columns.map(col => (
                      <td key={col} className="px-3 py-1.5 text-gray-700 max-w-[300px] truncate whitespace-nowrap" title={String(row[col] ?? '')}>
                        {row[col] === null ? <span className="text-gray-300 italic">NULL</span> : String(row[col])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !result && (
          <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
            Query results will appear here
          </div>
        )}
      </div>

      {/* History sidebar at bottom */}
      {history.length > 0 && (
        <div className="border-t flex-shrink-0" style={{ maxHeight: '120px', overflow: 'auto' }}>
          <div className="px-4 py-1.5 bg-gray-50 text-xs font-medium text-gray-500 sticky top-0">Recent Queries</div>
          {history.map((h, i) => (
            <button key={i} onClick={() => setSql(h.sql)}
              className="w-full text-left px-4 py-1 hover:bg-indigo-50 text-xs font-mono text-gray-600 truncate border-b border-gray-50">
              {h.sql.substring(0, 120)}{h.sql.length > 120 ? '...' : ''}
              <span className="text-gray-400 ml-2">({h.result?.duration || 0}ms)</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
